<template>
  <div class="space-y-6" data-test-id="payout-detail-content">
    <!-- Loading State -->
    <div
      v-if="store.isLoadingPayoutDetail"
      class="flex items-center justify-center py-8"
    >
      <LoadingSpinner :is-loading="true">
        <template #loading-text>Loading payout details...</template>
      </LoadingSpinner>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex items-center justify-center py-8">
      <div class="flex flex-col place-items-center gap-2">
        <XMarkIcon class="size-8 text-red-500" />
        <h3
          class="text-lg font-medium mb-2 text-surface-900 dark:text-surface-0"
          data-test-id="payout-not-found"
        >
          Payout Not Found
        </h3>
        <p class="text-sm text-surface-600 dark:text-surface-300 mb-4">
          {{ error }}
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else-if="store.selectedPayoutDetail">
      <!-- Payout Detail Panel -->
      <SectionPanel
        :title="`Balance Transactions for Payout ${store.selectedPayoutDetail.Id}`"
        test-id="payout-detail-panel"
      >
        <template #header-extra>
          <BadgePayoutStatus
            :status="store.selectedPayoutDetail.Status"
            :data-test-id="`payout-status-${store.selectedPayoutDetail.Id}`"
          />
        </template>
        <template #actions>
          <Button
            v-if="canAssignBankAccount"
            label="Assign Bank Account"
            icon="pi pi-plus"
            size="small"
            @click="showAssignmentDialog = true"
            data-test-id="assign-bank-account-btn"
            variant="link"
          />
          <Button
            as="a"
            v-if="shopifyPayoutUrl"
            :href="shopifyPayoutUrl"
            target="_blank"
            data-test-id="payout-link-button"
            icon="pi pi-external-link"
            size="small"
            label="View in Shopify"
          >
          </Button>
          <Button
            label="View Shopify Raw JSON"
            icon="pi pi-code"
            variant="outlined"
            size="small"
            @click="showRawJsonDialog = true"
            data-test-id="view-raw-json-btn"
          />
        </template>

        <div class="space-y-4">
          <dl class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div class="flex justify-between gap-4">
              <dt class="text-surface-600 dark:text-surface-300 font-medium">
                Payout ID:
              </dt>
              <dd
                class="font-mono font-semibold text-surface-900 dark:text-surface-0"
              >
                {{ store.selectedPayoutDetail.Id }}
              </dd>
            </div>

            <div class="flex justify-between gap-4">
              <dt class="text-surface-600 dark:text-surface-300 font-medium">
                Status:
              </dt>
              <dd>
                <BadgePayoutStatus
                  :status="store.selectedPayoutDetail.Status"
                  :data-test-id="`payout-status-badge-${store.selectedPayoutDetail.Id}`"
                />
              </dd>
            </div>

            <div class="flex justify-between gap-4">
              <dt class="text-surface-600 dark:text-surface-300 font-medium">
                Bank Account Assignment:
              </dt>
              <dd>
                <BadgeBankAccountAssignmentState
                  :state="
                    store.selectedPayoutDetail.BankAccountAssignmentState ||
                    'NO_BANK_ACCOUNT'
                  "
                  :data-test-id="`assignment-state-badge-${store.selectedPayoutDetail.Id}`"
                />
              </dd>
            </div>

            <div class="flex justify-between gap-4">
              <dt class="text-surface-600 dark:text-surface-300 font-medium">
                Amount:
              </dt>
              <dd class="font-semibold text-surface-900 dark:text-surface-0">
                {{
                  formatCurrency(
                    store.selectedPayoutDetail.Amount,
                    store.selectedPayoutDetail.Currency,
                  )
                }}
              </dd>
            </div>

            <div class="flex justify-between gap-4">
              <dt class="text-surface-600 dark:text-surface-300 font-medium">
                Issued At:
              </dt>
              <dd class="text-surface-900 dark:text-surface-0">
                {{
                  formatDateTimeForDisplay(store.selectedPayoutDetail.IssuedAt)
                }}
              </dd>
            </div>

            <div class="flex justify-between gap-4">
              <dt class="text-surface-600 dark:text-surface-300 font-medium">
                Store ID:
              </dt>
              <dd class="font-mono text-surface-900 dark:text-surface-0">
                {{ store.selectedPayoutDetail.StoreId }}
              </dd>
            </div>

            <div class="flex justify-between gap-4">
              <dt class="text-surface-600 dark:text-surface-300 font-medium">
                Business Entity:
              </dt>
              <dd class="text-surface-900 dark:text-surface-0">
                {{ getBusinessEntityDisplayName() }}
              </dd>
            </div>

            <div class="flex justify-between gap-4">
              <dt class="text-surface-600 dark:text-surface-300 font-medium">
                Bank Account:
              </dt>
              <dd class="text-surface-900 dark:text-surface-0 text-sm">
                <span v-if="hasBankAccount">{{ bankAccountDisplay }}</span>
                <span
                  v-else-if="!canAssignBankAccount"
                  class="text-surface-500 dark:text-surface-400 italic"
                >
                  Bank account needs to be assigned
                </span>
                <Button
                  v-else
                  variant="link"
                  @click="showAssignmentDialog = true"
                  data-test-id="assign-bank-account-link"
                >
                  Bank account needs to be assigned
                </Button>
              </dd>
            </div>

            <div v-if="assignmentAudit" class="flex justify-between gap-4">
              <dt class="text-surface-600 dark:text-surface-300 font-medium">
                Assignment Info:
              </dt>
              <dd class="text-sm text-surface-600 dark:text-surface-300">
                <AuditInfo :audit="assignmentAudit" :show-created="false" />
              </dd>
            </div>

            <div class="flex justify-between gap-4">
              <dt class="text-surface-600 dark:text-surface-300 font-medium">
                Transaction Type:
              </dt>
              <dd class="text-surface-900 dark:text-surface-0">
                {{
                  formatTransactionType(
                    store.selectedPayoutDetail.TransactionType,
                  )
                }}
              </dd>
            </div>
          </dl>
        </div>
      </SectionPanel>

      <!-- Balance Transactions Table -->
      <div class="py-6" data-test-id="balance-transactions-panel">
        <DataTable
          :value="store.balanceTransactions"
          :paginator="!store.isLoadingBalanceTransactions"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items "
          :rows="store.balanceTransactionsPageSize"
          :rowsPerPageOptions="[10, 20, 50, 100]"
          :sortable="false"
          class="p-datatable-sm"
          stripedRows
          responsiveLayout="scroll"
          scrollable
          scrollHeight="calc(100vh - 30rem)"
          :totalRecords="store.balanceTransactionsTotal"
          :lazy="true"
          filterDisplay="row"
          v-model:filters="tableFilters"
          @page="onBalanceTransactionsPageChange"
          @filter="onFilterChange"
          @row-click="onBalanceTransactionRowClick"
          rowHover
          data-test-id="balance-transactions-table"
        >
          <!-- Transaction ID Column -->
          <Column
            field="Id"
            header="Transaction ID"
            style="min-width: 12rem"
            filter
            filterField="Id"
            :showFilterMenu="false"
            :showFilterMatchModes="false"
            :showFilterOperator="false"
          >
            <template #body="{ data }">
              <div
                v-if="store.isLoadingBalanceTransactions"
                class="flex items-center"
              >
                <Skeleton width="8rem" height="1rem"></Skeleton>
              </div>
              <div
                v-else
                class="flex items-center gap-2 font-medium text-primary-600 hover:text-primary-500 dark:text-surface-0 dark:hover:text-surface-400 underline underline-offset-4 cursor-pointer"
                data-test-id="balance-transaction-id"
              >
                <ArrowsRightLeftIcon class="size-4 text-surface-400" />
                {{ data.Id }}
              </div>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <InputText
                v-model="filterModel.value"
                type="text"
                placeholder="Transaction ID"
                class="p-column-filter w-full"
                @input="debouncedIdFilter(filterCallback)"
                data-test-id="balance-txn-id-filter"
                size="small"
              />
            </template>
          </Column>

          <!-- Type Column -->
          <Column field="Type" header="Type" style="min-width: 8rem">
            <template #body="{ data }">
              <div v-if="store.isLoadingBalanceTransactions">
                <Skeleton width="5rem" height="1rem"></Skeleton>
              </div>
              <span
                v-else
                class="text-sm text-surface-600 dark:text-surface-300"
              >
                {{ formatTransactionType(data.Type) }}
              </span>
            </template>
          </Column>

          <!-- Amount Column -->
          <Column field="Amount" header="Amount" style="min-width: 8rem">
            <template #body="{ data }">
              <div v-if="store.isLoadingBalanceTransactions">
                <Skeleton width="5rem" height="1rem"></Skeleton>
              </div>
              <span
                v-else
                class="font-semibold"
                :class="
                  parseFloat(data.Amount) >= 0
                    ? 'text-surface-900 dark:text-surface-0'
                    : 'text-red-600 dark:text-red-400'
                "
              >
                {{ formatCurrency(parseFloat(data.Amount), data.Currency) }}
              </span>
            </template>
          </Column>

          <!-- Currency Column -->
          <Column field="Currency" header="Currency" style="min-width: 5rem">
            <template #body="{ data }">
              <div v-if="store.isLoadingBalanceTransactions">
                <Skeleton width="3rem" height="1rem"></Skeleton>
              </div>
              <span
                v-else
                class="text-sm text-surface-600 dark:text-surface-300"
              >
                {{ data.Currency }}
              </span>
            </template>
          </Column>

          <!-- Processing State Column -->
          <Column
            field="ProcessingState"
            header="Processing State"
            style="min-width: 8rem"
            filter
            filterField="ProcessingState"
            :showFilterMenu="false"
            :showFilterMatchModes="false"
            :showFilterOperator="false"
            data-test-id="balance-txn-processing-state-col"
          >
            <template #body="{ data }">
              <div v-if="store.isLoadingBalanceTransactions">
                <Skeleton width="5rem" height="1rem"></Skeleton>
              </div>
              <BadgeProcessingState
                v-else-if="data.ProcessingState"
                :status="data.ProcessingState"
                :data-test-id="`balance-txn-processing-state-${data.Id}`"
              />
              <span v-else class="text-surface-400 dark:text-surface-500"
                >—</span
              >
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <Select
                v-model="filterModel.value"
                :options="processingStateOptions"
                option-label="label"
                option-value="value"
                placeholder="Processing State"
                class="p-column-filter w-full"
                @change="debouncedProcessingStateFilter(filterCallback)"
                data-test-id="balance-txn-processing-state-filter"
                size="small"
                showClear
              />
            </template>
          </Column>

          <!-- Processed At Column -->
          <Column
            field="TransactionDate"
            header="Date"
            style="min-width: 10rem"
          >
            <template #body="{ data }">
              <div v-if="store.isLoadingBalanceTransactions">
                <Skeleton width="8rem" height="1rem"></Skeleton>
              </div>
              <span
                v-else
                class="text-sm text-surface-500 dark:text-surface-400"
              >
                {{ formatDateTimeForDisplay(data.TransactionDate) }}
              </span>
            </template>
          </Column>

          <!-- Empty state -->
          <template #empty>
            <div
              v-if="!store.isLoadingBalanceTransactions"
              class="flex flex-col items-center justify-center py-12"
              data-test-id="balance-transactions-empty-state"
            >
              <i
                class="pi pi-inbox text-4xl text-surface-300 dark:text-surface-600 mb-4"
              ></i>
              <p class="text-surface-500 dark:text-surface-400 text-lg">
                No balance transactions found
              </p>
            </div>
          </template>
        </DataTable>
      </div>
    </div>

    <!-- Bank Account Assignment Dialog -->
    <BankAccountAssignmentDialog
      v-if="store.selectedPayoutDetail"
      v-model:visible="showAssignmentDialog"
      :payout-id="store.selectedPayoutDetail.Id"
      :merchant-id="merchantId"
      :store-id="store.selectedPayoutDetail.StoreId"
      :business-entity-id="store.selectedPayoutDetail.BusinessEntityId"
      @assigned="handleBankAccountAssigned"
    />

    <!-- Raw JSON Dialog -->
    <Dialog
      v-model:visible="showRawJsonDialog"
      header="Shopify Raw JSON"
      :style="{ width: '95vw', maxWidth: '1600px' }"
      modal
      data-test-id="raw-json-dialog"
    >
      <!--
        Same 1600px cap the payouts drawer and the transaction-detail drawer
        use, so a payload tree gets the same line length wherever an operator
        reads it. At 800px the deeper Shopify nodes wrapped and the indent ate
        most of the width.

        The dialog body does the height bounding, and scrolls on both axes: a
        tree grows down as nodes expand and right as they nest.
      -->
      <div
        class="bg-surface-50 dark:bg-surface-950 rounded-lg p-4 overflow-auto max-h-[60vh]"
      >
        <JsonViewer
          :value="store.selectedPayoutDetail?.RawJson ?? null"
          empty-message="No raw JSON was recorded for this payout."
        />
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useFrontegg } from '@frontegg/vue'
import { useToast } from 'primevue/usetoast'
import { FilterMatchMode } from '@primevue/core/api'
import { useDebounceFn } from '@vueuse/core'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Skeleton from 'primevue/skeleton'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import { XMarkIcon, ArrowsRightLeftIcon } from '@heroicons/vue/24/outline'
import { errorToastWithDetails } from '@/util/toasts'
import { normalizeError } from '@/util/errorNormalizer'
import { formatDateTimeForDisplay } from '@/util/dateUtils'
import { formatCurrency } from '@/util/util'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import JsonViewer from '@/components/common/JsonViewer.vue'
import SectionPanel from '@/components/common/SectionPanel.vue'
import BadgePayoutStatus from '@/components/common/badges/BadgePayoutStatus.vue'
import BadgeProcessingState from '@/components/common/badges/BadgeProcessingState.vue'
import BadgeBankAccountAssignmentState from '@/components/common/badges/BadgeBankAccountAssignmentState.vue'
import AuditInfo from '@/components/common/AuditInfo.vue'
import BankAccountAssignmentDialog from './BankAccountAssignmentDialog.vue'
import { useShopifyPayoutsStore } from '@/stores/shopifyPayoutsStore'
import type {
  ShopifyPayoutDetail,
  ShopifyPayoutSummary,
  ShopifyBalanceTransactionSummary,
  ShopifyBalanceTransactionProcessingState,
} from '@/types/shopifyPayouts'
import type { AuditRecord } from '@/types/audit'

interface Props {
  merchantId: string
  payout: ShopifyPayoutSummary
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'select-balance-transaction': [
    transaction: ShopifyBalanceTransactionSummary,
    payoutDetail: ShopifyPayoutDetail,
  ]
}>()

const toast = useToast()
const { authState } = useFrontegg()
const store = useShopifyPayoutsStore()

const error = ref<string | null>(null)
const showRawJsonDialog = ref(false)
const showAssignmentDialog = ref(false)

// Filter state for PrimeVue DataTable
const tableFilters = ref<Record<string, { value: unknown; matchMode: string }>>(
  {
    Id: { value: null, matchMode: FilterMatchMode.EQUALS },
    ProcessingState: {
      value: store.balanceTransactionProcessingStateFilter,
      matchMode: FilterMatchMode.EQUALS,
    },
  },
)

// Processing state filter options
const processingStateOptions: {
  label: string
  value: ShopifyBalanceTransactionProcessingState | null
}[] = [
  { label: 'All', value: null },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Pending Confirmation', value: 'PENDING_CONFIRMATION' },
  { label: 'Processing', value: 'PROCESSING' },
  { label: 'Processed', value: 'PROCESSED' },
  { label: 'Skipped', value: 'SKIPPED' },
  { label: 'Failed', value: 'FAILED' },
]

// Debounced filter callbacks
const debouncedIdFilter = useDebounceFn((filterCallback: () => void) => {
  filterCallback()
}, 500)

const debouncedProcessingStateFilter = useDebounceFn(
  (filterCallback: () => void) => {
    filterCallback()
  },
  500,
)

// Computed for View in Shopify button
const shopifyPayoutUrl = computed(
  () => store.selectedPayoutDetail?.Link ?? null,
)

function formatTransactionType(type: string): string {
  return store.formatTransactionType(type)
}

const canAssignBankAccount = computed(() => {
  if (!store.selectedPayoutDetail) return false
  return store.canAssignBankAccount(store.selectedPayoutDetail)
})

const hasBankAccount = computed(() => {
  return (
    !!store.selectedPayoutDetail?.BankAccountId ||
    !!store.selectedPayoutDetail?.AssignedBankAccountId
  )
})

const assignmentAudit = computed<AuditRecord | null>(() => {
  if (
    !store.selectedPayoutDetail?.BankAccountAssignedBy ||
    !store.selectedPayoutDetail?.BankAccountAssignedAt
  ) {
    return null
  }

  return {
    LastModifiedBy: store.selectedPayoutDetail.BankAccountAssignedBy,
    LastModifiedOn: store.selectedPayoutDetail.BankAccountAssignedAt,
  }
})

function getBusinessEntityDisplayName(): string {
  return store.getBusinessEntityDisplayName(
    store.selectedPayoutDetail?.BusinessEntityId,
    props.merchantId,
  )
}

const bankAccountDisplay = computed(() => {
  if (!store.selectedPayoutDetail) return '-'
  const display = store.getBankAccountDisplayName(
    store.selectedPayoutDetail,
    props.merchantId,
  )
  return display || '-'
})

async function handleBankAccountAssigned() {
  if (props.merchantId && props.payout) {
    try {
      store.resetPayoutDetailCache()
      await loadPayoutDetail()

      await loadBalanceTransactions(1)
    } catch (error: unknown) {
      const apiError = normalizeError(error)
      errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
    }
  }
}

async function loadPayoutDetail() {
  if (!props.merchantId || !props.payout) {
    error.value = 'Missing required parameters'
    return
  }

  error.value = null

  try {
    const detail = await store.loadPayoutDetail(
      authState,
      props.merchantId,
      props.payout.StoreId,
      props.payout.BusinessEntityId,
      props.payout.Id,
    )

    // Load balance transactions after payout detail is loaded
    if (detail && detail.Id === props.payout.Id) {
      const balanceTransactionsLoaded =
        store.lastLoadedBalanceTransactionsPayoutId === detail.Id &&
        store.balanceTransactions.length > 0

      if (!balanceTransactionsLoaded) {
        await loadBalanceTransactions()
      }
    }
  } catch (err: unknown) {
    const apiError = normalizeError(err)
    error.value = apiError.toToastMessage()
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  }
}

async function loadBalanceTransactions(page: number = 1, id?: string) {
  if (!store.selectedPayoutDetail || !props.merchantId || !props.payout) return

  try {
    const normalizedId =
      typeof id === 'string' && id.trim().length > 0 ? id.trim() : undefined

    await store.loadBalanceTransactions(
      authState,
      props.merchantId,
      store.selectedPayoutDetail.StoreId,
      store.selectedPayoutDetail.BusinessEntityId,
      props.payout.Id,
      page,
      undefined, // use default page size
      'TransactionDate,desc', // sort by newest date first
      store.balanceTransactionProcessingStateFilter,
      normalizedId,
    )
  } catch (err: unknown) {
    const apiError = normalizeError(err)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  }
}

// Handle filter changes
function onFilterChange() {
  const processingState = tableFilters.value.ProcessingState?.value as
    | ShopifyBalanceTransactionProcessingState
    | null
    | undefined

  // Update store with filter value
  store.setBalanceTransactionProcessingStateFilter(processingState ?? null)

  // Get and normalize ID filter value
  const rawId = tableFilters.value.Id?.value as string | null | undefined
  const normalizedId =
    typeof rawId === 'string' && rawId.trim().length > 0
      ? rawId.trim()
      : undefined

  // Update store with normalized ID filter (or null when cleared)
  store.setBalanceTransactionIdFilter(normalizedId ?? null)

  // Reload balance transactions with new filters
  loadBalanceTransactions(1, normalizedId)
}

function onBalanceTransactionsPageChange(event: {
  page: number
  rows: number
}) {
  const newPage = event.page + 1 // PrimeVue uses 0-based indexing
  if (event.rows !== store.balanceTransactionsPageSize) {
    store.setBalanceTransactionsPageSize(event.rows)
  }

  // Preserve normalized ID filter when paginating
  const rawId = tableFilters.value.Id?.value as string | null | undefined
  const normalizedId =
    typeof rawId === 'string' && rawId.trim().length > 0
      ? rawId.trim()
      : undefined

  loadBalanceTransactions(newPage, normalizedId)
}

function onBalanceTransactionRowClick(event: {
  data: ShopifyBalanceTransactionSummary
}) {
  if (store.selectedPayoutDetail) {
    emit('select-balance-transaction', event.data, store.selectedPayoutDetail)
  }
}

// Load payout detail when payout changes
watch(
  () => props.payout,
  async (newPayout, oldPayout) => {
    // Reload when the payout identity changes. A payout is identified by its
    // store + business entity + id (two entities can share a numeric Id), so
    // compare the full tuple — an Id-only check would miss an entity switch.
    const identityChanged =
      newPayout &&
      (newPayout.Id !== oldPayout?.Id ||
        newPayout.StoreId !== oldPayout?.StoreId ||
        newPayout.BusinessEntityId !== oldPayout?.BusinessEntityId)
    if (identityChanged) {
      // Reset filters when payout changes
      tableFilters.value.Id = {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
      }
      tableFilters.value.ProcessingState = {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
      }
      await loadPayoutDetail()
    }
  },
  { immediate: true },
)

// Watch for payout detail to become available
watch(
  () => store.selectedPayoutDetail,
  async (newDetail, oldDetail) => {
    const detailChanged =
      !oldDetail || (newDetail && newDetail.Id !== oldDetail.Id)

    if (newDetail && newDetail.Id === props.payout?.Id && detailChanged) {
      // Check if balance transactions are already loaded for this payout
      const balanceTransactionsLoaded =
        store.lastLoadedBalanceTransactionsPayoutId === newDetail.Id &&
        store.balanceTransactions.length > 0

      if (!balanceTransactionsLoaded) {
        await loadBalanceTransactions()
      }
    }
  },
  { immediate: true },
)
</script>

<style scoped></style>
